/**
 * TopBetsTable - Displays top bets for a specific game in a data grid
 *
 * Features:
 * - Reuses existing column definitions from props table
 * - Filters and sorts bets by EV %
 * - Supports team filtering
 * - Add to bet slip functionality
 */
import React, { useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, useMediaQuery, useTheme } from 'omaha-design-system';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { CaretDown } from '@phosphor-icons/react';
import { DisplayGame, BetSlipItem } from '../../data/types';
import { getPropsColumns, getMobilePropsColumns, PropBet } from '../columns';
import { addToBetSlip, removeFromBetSlip } from '../../store/slice';
import { IRootState } from '../../../../store/reducer';
import { OtherBooksBottomSheet } from '../OtherBooksBottomSheet';
import { ExpandedOddsPanel } from '../ExpandedOddsPanel';
import { CalculateEVResponse } from '../../data/evTypes';
import { STAT_LABELS, HIDDEN_STATS } from '../../data/statLabels';
import { leagueHasMinutes, leagueHidesQuestionable } from '../../data/leagueConfig';

interface TopBetsTableProps {
  game: DisplayGame;
  teamFilter: 'all' | 'away' | 'home';
}

const TopBetsTable: React.FC<TopBetsTableProps> = ({ game, teamFilter }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const betSlip = useSelector((state: IRootState) => state.stokasticProps.betSlip);
  const filters = useSelector((state: IRootState) => state.stokasticProps.filters);
  const playerCustomEVData = useSelector((state: IRootState) => state.stokasticProps.playerCustomEVData);
  const playerCustomizations = useSelector((state: IRootState) => state.stokasticProps.playerCustomizations);

  const [otherBooksSheetOpen, setOtherBooksSheetOpen] = useState(false);
  const [selectedOtherBooksBet, setSelectedOtherBooksBet] = useState<PropBet | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);

  const getDetailPanelContent = useCallback(
    (params: { row: PropBet }) => <ExpandedOddsPanel row={params.row} />,
    []
  );
  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const handleRowClick = useCallback((params: { id: string | number }) => {
    setExpandedRowIds((prev) =>
      prev.includes(String(params.id))
        ? prev.filter((id) => id !== String(params.id))
        : [...prev, String(params.id)]
    );
  }, []);

  // Helper to get custom EV values for a player's stat/side/book from Redux
  const getCustomEVValues = useCallback(
    (playerId: string, stat: string, side: 'Over' | 'Under', bookCode: string) => {
      const customData = playerCustomEVData[playerId];
      if (!customData?.outcomes) return null;

      // Normalize stat name for matching (handles "Points" vs "PTS", etc.)
      const normalized = STAT_LABELS[stat] || stat;

      // Find matching outcome - try both raw stat and normalized version
      const outcome = customData.outcomes.find(
        (o) =>
          (o.statType === stat || o.statType === normalized || STAT_LABELS[o.statType] === normalized) &&
          o.label === side
      );
      if (!outcome) return null;

      // Find matching sportsbook
      const sbResult = outcome.sportsbooks.find((sb) => sb.code === bookCode);
      if (!sbResult) return null;

      return {
        evPct: sbResult.blendedEV != null ? sbResult.blendedEV * 100 : null, // Convert to percentage
        modelWin: sbResult.blendedTrueWinProbability,
      };
    },
    [playerCustomEVData]
  );

  // Transform game data into PropBet format
  const allBets = useMemo(() => {
    const bets: PropBet[] = [];

    for (const player of game.players || []) {
      // Find injured teammates for this player
      const injuredTeammates = (game.players || [])
        .filter((p) => {
          const playerMinutes =
            typeof p.minutes === 'string' ? parseFloat(p.minutes) : Number(p.minutes);
          return (
            p.team === player.team &&
            p.id !== player.id &&
            p.injuryStatus &&
            ['out', 'doubtful', 'questionable'].includes(p.injuryStatus.toLowerCase()) &&
            !isNaN(playerMinutes) &&
            playerMinutes > 0
          );
        })
        .map((p) => ({
          name: p.name,
          status: p.injuryStatus,
          notes: p.injuryNotes,
        }));

      // Check if player has custom projections
      const customization = playerCustomizations[player.id];
      const hasCustomizations = !!customization;
      const customMinutes = customization?.minutes;

      for (const prop of player.props || []) {
        if (prop.sportsbooks.length === 0) continue;

        const best = prop.sportsbooks[0];

        // Check if there's custom EV data for this player/stat/side/book
        const customValues = getCustomEVValues(player.id, prop.stat, best.side, best.book);

        bets.push({
          stat: prop.stat,
          originalStat: prop.originalStat,
          projection: prop.projection,
          sportsbooks: prop.sportsbooks,
          book: best.book,
          bookIcon: best.bookIcon,
          bookLine: best.line,
          odds: best.americanOdds,
          oddsNum: best.odds,
          delta: best.delta,
          edge: best.edge,
          bookWin: best.bookWin,
          modelWin: customValues?.modelWin ?? best.modelWin,
          marketWin: best.marketWin,
          stokWin: best.stokWin,
          evPct: customValues?.evPct ?? best.evPct,
          side: best.side,
          deepLinkUrl: best.deepLinkUrl,
          playerName: player.name,
          playerId: player.id,
          team: player.team,
          avatar: player.avatar,
          position: player.position,
          minutes: player.minutes,
          customMinutes,
          hasCustomizations,
          projections: player.projections,
          injuryStatus: player.injuryStatus,
          injuryNotes: player.injuryNotes,
          battingOrder: player.battingOrder,
          confirmedLineup: player.confirmedLineup,
          hand: player.hand,
          injuredTeammates,
          matchup: game.matchup,
          startDate: game.time,
        });
      }
    }

    return bets;
  }, [game, getCustomEVValues, playerCustomizations]);

  // Filter bets by team
  const filteredBets = useMemo(() => {
    let bets = allBets;

    // Apply team filter
    if (teamFilter !== 'all') {
      const targetTeam =
        teamFilter === 'away' ? game.awayTeam.abbr : game.homeTeam.abbr;
      bets = bets.filter(
        (bet) => bet.team.toUpperCase() === targetTeam.toUpperCase()
      );
    }

    // Apply minimum edge filter
    bets = bets.filter((bet) => bet.evPct >= filters.minEdge);

    // Apply position filter (NFL)
    if (filters.positionFilter && filters.positionFilter !== 'All') {
      // Ignore a position none of this game's players has (stale/persisted value) instead of hiding every bet
      const wanted = filters.positionFilter.toUpperCase();
      if (bets.some((bet) => bet.position?.toUpperCase() === wanted)) {
        bets = bets.filter((bet) => bet.position?.toUpperCase() === wanted);
      }
    }

    // Apply minimum minutes filter (use custom minutes if available) - only for leagues with minutes
    if (leagueHasMinutes(filters.league)) {
      bets = bets.filter((bet) => {
        const playerMinutes = bet.customMinutes != null
          ? bet.customMinutes
          : (typeof bet.minutes === 'string' ? parseFloat(bet.minutes) : Number(bet.minutes));
        return !isNaN(playerMinutes) && playerMinutes >= filters.minMinutes;
      });
    }

    // Filter out outcomes without market projections
    bets = bets.filter((bet) => bet.marketWin && bet.marketWin > 0);

    // Filter out hidden stat types
    bets = bets.filter((bet) => !HIDDEN_STATS.has(bet.stat));

    // Filter out questionable injury status (not in the NFL, where it's a routine designation)
    if (leagueHidesQuestionable(filters.league)) {
      bets = bets.filter((bet) => bet.injuryStatus?.toLowerCase() !== 'questionable');
    }

    // Max EV filter
    if (filters.maxEV > 0) {
      bets = bets.filter((bet) => bet.evPct <= filters.maxEV);
    }

    // Min odds filter (American odds)
    if (filters.minOdds !== 0) {
      bets = bets.filter((bet) => {
        const oddsNum = parseInt(bet.odds.replace('+', ''), 10);
        return !isNaN(oddsNum) && oddsNum >= filters.minOdds;
      });
    }

    // Max odds filter (American odds)
    if (filters.maxOdds !== 0) {
      bets = bets.filter((bet) => {
        const oddsNum = parseInt(bet.odds.replace('+', ''), 10);
        return !isNaN(oddsNum) && oddsNum <= filters.maxOdds;
      });
    }

    // Injured teammate filter
    if (filters.hideInjuredTeammate) {
      bets = bets.filter((bet) => !bet.injuredTeammates || bet.injuredTeammates.length === 0);
    }

    // Both models +EV filter
    if (filters.requireBothModelsEV) {
      bets = bets.filter((bet) => bet.stokWin > bet.bookWin && bet.marketWin > bet.bookWin);
    }

    return bets;
  }, [allBets, teamFilter, game.awayTeam.abbr, game.homeTeam.abbr, filters]);

  // Sort by EV % descending
  const sortedBets = useMemo(() => {
    return [...filteredBets].sort((a, b) => b.evPct - a.evPct);
  }, [filteredBets]);

  // Add row index for locked state check (not used here, but required by columns)
  const rowsWithIndex = useMemo(() => {
    return sortedBets.map((bet, index) => ({
      ...bet,
      id: `${bet.playerId}-${bet.stat}`,
      _rowIndex: index,
    }));
  }, [sortedBets]);

  const handleAddToBetSlip = useCallback(
    (bet: BetSlipItem) => {
      dispatch(addToBetSlip(bet));
    },
    [dispatch]
  );

  const handleRemoveFromBetSlip = useCallback(
    (index: number) => {
      dispatch(removeFromBetSlip(index));
    },
    [dispatch]
  );

  const handleOtherBooksClick = useCallback((bet: PropBet) => {
    setSelectedOtherBooksBet(bet);
    setOtherBooksSheetOpen(true);
  }, []);

  const handleOtherBooksClose = useCallback(() => {
    setOtherBooksSheetOpen(false);
    setSelectedOtherBooksBet(null);
  }, []);

  const columns = useMemo(() => {
    if (isMobile) {
      return getMobilePropsColumns(
        handleAddToBetSlip,
        betSlip,
        handleRemoveFromBetSlip,
        handleOtherBooksClick
      );
    }
    return getPropsColumns(
      handleAddToBetSlip,
      betSlip,
      handleRemoveFromBetSlip,
      false, // isTeaser — the matchup page is subscriber-only
      filters.league
    );
  }, [isMobile, handleAddToBetSlip, betSlip, handleRemoveFromBetSlip, handleOtherBooksClick, filters.league]);

  // Check if empty state is specifically caused by injured teammate filter
  const isEmptyDueToInjuredTeammate = sortedBets.length === 0 && filters.hideInjuredTeammate && allBets.length > 0;

  if (sortedBets.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
        }}
      >
        {isEmptyDueToInjuredTeammate ? (
          <>
            <Typography>All players hidden by the "Hide Questionable Impact" filter.</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Disable "Hide Questionable Impact" in Filters to see bets for this game.
            </Typography>
          </>
        ) : (
          <>
            <Typography>No bets found matching your filters.</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Try adjusting your filters.
            </Typography>
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          ...(!isMobile && { flex: 1 }),
          minHeight: isMobile ? 0 : 400,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflow: isMobile ? 'visible' : 'hidden',
          '& .MuiDataGrid-root': {
            border: 'none',
            backgroundColor: isMobile ? 'transparent' : '#161617',
            borderRadius: isMobile ? 0 : 2,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            color: '#f1f2f2',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1C1C1E',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          },
          '& .MuiDataGrid-columnHeader': {
            color: '#f2f2f3b2',
            '&:focus': { outline: 'none' },
            '&:focus-within': { outline: 'none' },
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            fontSize: 13,
          },
          '& .MuiDataGrid-row': {
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'rgba(42, 83, 195, 0.16)',
            '&:hover': { backgroundColor: 'rgba(42, 83, 195, 0.24)' },
          },
          '& .MuiTablePagination-root': {
            color: '#f2f2f3b2',
          },
          '& .MuiTablePagination-selectIcon': {
            color: '#f2f2f3b2',
          },
          '& .MuiIconButton-root': {
            color: '#f2f2f3b2',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: isMobile ? 'transparent' : '#161617',
          },
          '& .MuiDataGrid-sortIcon': {
            color: '#42a5f5',
          },
          '& .MuiDataGrid-footerContainer': {
            justifyContent: 'flex-start',
            borderTop: 'none',
            minHeight: 'auto',
          },
          '& .MuiTablePagination-spacer': {
            display: 'none',
          },
          '& .MuiTablePagination-selectLabel': {
            color: 'rgba(242, 242, 243, 0.7)',
          },
          '& .MuiTablePagination-displayedRows': {
            color: '#F2F2F3',
          },
          '& .MuiTablePagination-select, & .MuiTablePagination-selectIcon, & .MuiTablePagination-actions .MuiIconButton-root': {
            color: '#42A5F5',
          },
          '& .MuiTablePagination-toolbar': {
            minHeight: 'auto',
            padding: '8px 0',
            marginLeft: '16px',
          },
          '& .MuiTablePagination-toolbar .MuiInputBase-root': {
            border: '1px solid #42A5F5',
            borderRadius: '8px',
            height: 32,
            minHeight: 32,
            '& .MuiSelect-select': {
              padding: '4px 24px 4px 8px',
              height: 'auto',
              minHeight: 'auto',
            },
          },
          '& .MuiTablePagination-actions .MuiIconButton-root': {
            border: '1px solid #42A5F5',
            borderRadius: '8px',
            height: 32,
            width: 32,
            padding: 0,
            margin: 0,
          },
          '& .MuiTablePagination-actions .MuiIconButton-root:last-of-type': {
            marginLeft: '8px',
          },
        }}
      >
        <DataGridPro
          rows={rowsWithIndex}
          columns={columns}
          autoHeight={isMobile}
          rowHeight={isMobile ? undefined : 56}
          getRowHeight={isMobile ? () => 'auto' as const : undefined}
          columnHeaderHeight={isMobile ? 0 : 56}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooterSelectedRowCount
          pagination={!isMobile}
          pageSizeOptions={isMobile ? [1000] : [10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: {
              sortModel: [{ field: 'evPct', sort: 'desc' }],
            },
          }}
          hideFooter={isMobile}
          {...(!isMobile && {
            getDetailPanelContent,
            getDetailPanelHeight,
            detailPanelExpandedRowIds: expandedRowIds,
            onDetailPanelExpandedRowIdsChange: (ids: (string | number)[]) => setExpandedRowIds(ids.map(String)),
            onRowClick: handleRowClick,
            slots: {
              detailPanelExpandIcon: () => <CaretDown size={16} weight="bold" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />,
              detailPanelCollapseIcon: () => <CaretDown size={16} weight="bold" style={{ color: 'rgba(255, 255, 255, 0.5)', transform: 'rotate(180deg)' }} />,
            },
          })}
          sx={{
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
            ...(isMobile && {
              border: 'none',
              '& .MuiDataGrid-columnHeaders': { display: 'none' },
              '& .MuiDataGrid-virtualScroller': {
                marginTop: '0 !important',
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                width: '100% !important',
                maxWidth: '100% !important',
                overflow: 'hidden',
              },
              '& .MuiDataGrid-virtualScrollerRenderZone': {
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
              },
              '& .MuiDataGrid-row': {
                borderBottom: 'none',
                width: '100%',
                maxWidth: '100%',
                marginBottom: '4px',
                backgroundColor: 'transparent',
                overflow: 'hidden',
                boxSizing: 'border-box',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                  padding: '4px 16px',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: '0 !important',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                },
              },
              '& .MuiDataGrid-footerContainer': { display: 'none' },
              '& .MuiDataGrid-overlay': {
                backgroundColor: 'transparent',
              },
            }),
          }}
        />
      </Box>

      {/* Other Books Bottom Sheet for mobile */}
      {selectedOtherBooksBet && (
        <OtherBooksBottomSheet
          open={otherBooksSheetOpen}
          onClose={handleOtherBooksClose}
          playerName={selectedOtherBooksBet.playerName}
          playerId={selectedOtherBooksBet.playerId}
          avatar={selectedOtherBooksBet.avatar}
          stat={selectedOtherBooksBet.stat}
          side={selectedOtherBooksBet.side}
          projection={selectedOtherBooksBet.projection}
          sportsbooks={selectedOtherBooksBet.sportsbooks}
          onAddToBetSlip={handleAddToBetSlip}
          betSlip={betSlip}
        />
      )}
    </>
  );
};

export default TopBetsTable;
